// NEW_FILE_JUSTIFIED: Extracted from App.tsx to fix no-giant-component diagnostic
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: Convex generated API
import { useCompanyContext } from '@/hooks/useCompanyContext';
import { SignInPage } from '@/components/auth/SignInPage';
import { SignUpPage } from '@/components/auth/SignUpPage';
import { PrivacyPolicyPage } from '@/pages/legal/PrivacyPolicyPage';
import { TermsOfServicePage } from '@/pages/legal/TermsOfServicePage';
import { CookiesPolicyPage } from '@/pages/legal/CookiesPolicyPage';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { InviteAcceptPage } from '@/pages/InviteAcceptPage';
import { CompanyInviteAcceptPage } from '@/pages/CompanyInviteAcceptPage';
import {
  LeadsPageWrapper,
  LeadsBoardPageWrapper,
  PostWriterPageWrapper,
  PostsSchedulePageWrapper,
  BusinessSettingsPageWrapper,
  CalendarPageWrapper,
  WritingStylesPageWrapper,
  TemplatesPageWrapper,
  AIAvatarPageWrapper,
  MyAvatarsPageWrapper,
  AvatarFolderPageWrapper,
  AvatarGeneratePageWrapper,
  VoiceClonePageWrapper,
  MyVoicesPageWrapper,
  ConnectedAccountsPageWrapper,
  AiModelsSettingsPageWrapper,
  SettingsPromptsPageWrapper,
  ScheduledPostsPageWrapper,
  PostGenerationMultiAIPageWrapper,
  PostEditorPageWrapper,
  ReelGenerationPageWrapper,
  ReelEditorPageWrapper,
  PropertyVideoPageWrapper,
  PropertyListDraftPageWrapper,
  PropertyListReadyPageWrapper,
  PropertyDetailPageWrapper,
  PropertyNewPageWrapper,
  VideoSettingsPageWrapper,
  PostsListPageWrapper,
  ReelsListPageWrapper,
  PhotostudioPageWrapper,
  PhotostudioProjectsPageWrapper,
  PhotostudioProjectPageWrapper,
  PhotostudioElementsPageWrapper,
  AIStudioPageWrapper,
  HairstyleListPageWrapper,
  TelegramPageWrapper,
  UnifiedInboxPageWrapper,
  GmailPageWrapper,
  WhatsAppPageWrapper,
  AutomationSettingsPageWrapper,
  AiRepliesSettingsPageWrapper,
  OrganisationSettingsPageWrapper,
  PoliciesListPageWrapper,
  RenewalListPageWrapper,
  BulkRenewalImportPageWrapper,
  PolicyFormPageWrapper,
  InsuranceTypesListPageWrapper,
  CreateTypeStep1PageWrapper,
  TypeBuilderPageWrapper,
  MigrationPageWrapper,
  ExtractPolicyPageWrapper,
  ReviewPublishPageWrapper,
  ProvidersListPageWrapper,
  ProviderFormPageWrapper,
  LeadDetailPageWrapper,
  CustomersListPageWrapper,
  CustomerDetailPageWrapper,
  CompanyEmployeesListPageWrapper,
  CompanyNewEmployeePageWrapper,
  CompanyEditEmployeePageWrapper,
  CompanyEmployeeDetailPageWrapper,
  CompanyPoliciesListPageWrapper,
  CompanyPolicyDetailPageWrapper,
  CompanyInvoicesListPageWrapper,
  CompanyInvoiceDetailPageWrapper,
  CompanyManagersPageWrapper,
  CompanySettingsPageWrapper,
  CompaniesListPageWrapper,
  CompanyDetailPageWrapper,
  BankAccountsPageWrapper,
  InvoicesListPageWrapper,
  InvoiceFormPageWrapper,
  InsuranceEmployeeDetailPageWrapper,
  PaymentsListPageWrapper,
  ClaimsListPageWrapper,
  InsuranceSettingsPageWrapper,
  DashboardPageWrapper,
  InsuranceKnowledgeBasePageWrapper,
  RealEstateKnowledgeBasePageWrapper,
  VoiceAgentsListPageWrapper,
  VoiceCallHistoryPageWrapper,
  VoiceCallDetailPageWrapper,
  VoicePhoneNumbersPageWrapper,
  VoiceSipTrunksPageWrapper,
} from '@/components/routing/PageWrappers';
import {
  ControlDashboardPageWrapper,
  UsersManagementPageWrapper,
  OrganisationsManagementPageWrapper,
  OrgDetailPageWrapper,
  PolicyTemplatesPageWrapper,
  TemplateDetailPageWrapper,
  AuditLogPageWrapper,
  OrgPolicyStatsPageWrapper,
} from '@/components/routing/ControlPageWrappers';
import {
  BenefitPlansListPageWrapper,
  BenefitPlanFormPageWrapper,
  BenefitPlanDetailPageWrapper,
  ClaimDetailPageWrapper,
  PreAuthListPageWrapper,
  PreAuthDetailPageWrapper,
  ClaimFormsListPageWrapper,
  ClaimFormEditorPageWrapper,
  MyInsurancePageWrapper,
  MyCoveragePageWrapper,
  MyClaimsListPageWrapper,
  NewClaimPageWrapper,
  MyClaimDetailPageWrapper,
  MyPreAuthListPageWrapper,
  NewPreAuthPageWrapper,
  MyPreAuthDetailPageWrapper,
  CompanyClaimsSummaryPageWrapper,
  CustomerApplyPageWrapper,
  MyApplicationsPageWrapper,
  PolicyDraftsPageWrapper,
  BenefitPlanPreviewPageWrapper,
} from '@/components/routing/ClaimsPageWrappers';
import { ChatSessionHistoryPageWrapper, ChatSessionDetailPageWrapper } from './ChatPageWrappers';

interface IAppRoutesProps {
  isAuthenticated: boolean;
}

function DefaultRedirect() {
  const { isCompanyUser, isLoading } = useCompanyContext();
  const currentUser = useQuery(api.users.queries.getCurrentUser);
  const pendingInvitation = useQuery(
    api.companies.invitations.getMyPendingInvitation,
    currentUser?.role === 'company_user' && !isCompanyUser ? {} : 'skip'
  );

  if (isLoading || currentUser === undefined) return null;

  // Already a company member — go to company portal
  if (isCompanyUser) return <Navigate to="/company/my-insurance" replace />;

  // Has company_user role but no membership yet — redirect to accept invitation
  if (currentUser?.role === 'company_user') {
    if (pendingInvitation === undefined) return null; // still loading
    if (pendingInvitation) {
      return <Navigate to={`/company-invite/${pendingInvitation.token}`} replace />;
    }
    return <Navigate to="/company/my-insurance" replace />;
  }

  return <Navigate to="/leads" replace />;
}

function PostSignupRedirect() {
  const currentUser = useQuery(api.users.queries.getCurrentUser);
  if (currentUser === undefined) return null;
  if (currentUser?.role === 'company_user') return <DefaultRedirect />;
  return <Navigate to="/onboarding" replace />;
}

function OrgOnlyRoute() {
  const { isCompanyUser, isLoading } = useCompanyContext();
  const currentUser = useQuery(api.users.queries.getCurrentUser);
  if (isLoading || currentUser === undefined) return null;
  // Block both active company members AND users with company_user role (pre-invitation-accept)
  if (isCompanyUser || currentUser?.role === 'company_user') return <DefaultRedirect />;
  return <Outlet />;
}

export function AppRoutes({ isAuthenticated }: IAppRoutesProps) {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/signin" element={!isAuthenticated ? <SignInPage /> : <DefaultRedirect />} />
      <Route path="/signup" element={!isAuthenticated ? <SignUpPage /> : <PostSignupRedirect />} />

      {/* Legal pages - accessible without authentication */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/cookies" element={<CookiesPolicyPage />} />

      {/* Invite route - semi-public */}
      <Route path="/invite/:token" element={<InviteAcceptPage />} />

      {/* Org-only protected routes — company users are redirected to /company/employees */}
      <Route element={isAuthenticated ? <OrgOnlyRoute /> : <Navigate to="/signin" replace />}>
        <Route path="/leads" element={<LeadsPageWrapper />} />
        <Route path="/leads/board" element={<LeadsBoardPageWrapper />} />
        <Route path="/leads/:leadId" element={<LeadDetailPageWrapper />} />
        <Route path="/messages" element={<UnifiedInboxPageWrapper />} />
        <Route path="/messages/telegram" element={<TelegramPageWrapper />} />
        <Route path="/messages/gmail" element={<GmailPageWrapper />} />
        <Route path="/messages/gmail/compose" element={<GmailPageWrapper />} />
        <Route path="/messages/gmail/thread/:threadId" element={<GmailPageWrapper />} />
        <Route path="/messages/whatsapp" element={<WhatsAppPageWrapper />} />
        <Route path="/post-writer" element={<PostWriterPageWrapper />} />
        <Route path="/posts-schedule" element={<PostsSchedulePageWrapper />} />
        <Route path="/business-settings" element={<BusinessSettingsPageWrapper />} />
        <Route path="/connected-accounts" element={<ConnectedAccountsPageWrapper />} />
        <Route path="/ai-models" element={<AiModelsSettingsPageWrapper />} />
        <Route path="/automation" element={<AutomationSettingsPageWrapper />} />
        <Route path="/settings/ai-replies" element={<AiRepliesSettingsPageWrapper />} />
        <Route path="/settings/prompts" element={<SettingsPromptsPageWrapper />} />
        <Route path="/scheduled-posts" element={<ScheduledPostsPageWrapper />} />
        <Route path="/scheduled" element={<CalendarPageWrapper />} />
        <Route path="/writing-styles" element={<WritingStylesPageWrapper />} />
        <Route path="/templates" element={<TemplatesPageWrapper />} />
        <Route path="/ai-avatar" element={<Navigate to="/ai-avatar/reference-persons" replace />} />
        <Route path="/ai-avatar/reference-persons" element={<AIAvatarPageWrapper />} />
        <Route path="/ai-avatar/my-avatars" element={<MyAvatarsPageWrapper />} />
        <Route path="/ai-avatar/my-avatars/generate" element={<AvatarGeneratePageWrapper />} />
        <Route
          path="/ai-avatar/my-avatars/folder/:folderId"
          element={<AvatarFolderPageWrapper />}
        />
        <Route path="/voices/clone" element={<VoiceClonePageWrapper />} />
        <Route path="/voices/my-voices" element={<MyVoicesPageWrapper />} />
        <Route path="/photostudio" element={<PhotostudioPageWrapper />} />
        <Route path="/photostudio/projects" element={<PhotostudioProjectsPageWrapper />} />
        <Route
          path="/photostudio/projects/:projectId"
          element={<PhotostudioProjectPageWrapper />}
        />
        <Route path="/photostudio/elements" element={<PhotostudioElementsPageWrapper />} />
        <Route path="/ai-studio" element={<AIStudioPageWrapper />} />
        <Route path="/posts" element={<PostsListPageWrapper />} />
        <Route path="/reels" element={<ReelsListPageWrapper />} />
        <Route path="/hairstyle" element={<HairstyleListPageWrapper />} />
        <Route path="/post-generation/:topicId" element={<PostGenerationMultiAIPageWrapper />} />
        <Route path="/post/:postId" element={<PostEditorPageWrapper />} />
        <Route path="/reel-generation/:topicId" element={<ReelGenerationPageWrapper />} />
        <Route path="/reel/:reelId" element={<ReelEditorPageWrapper />} />
        <Route path="/property-video" element={<PropertyVideoPageWrapper />} />
        <Route path="/property/draft" element={<PropertyListDraftPageWrapper />} />
        <Route path="/property/ready" element={<PropertyListReadyPageWrapper />} />
        <Route path="/property/new" element={<PropertyNewPageWrapper />} />
        <Route path="/property/video-settings" element={<VideoSettingsPageWrapper />} />
        <Route path="/property/:propertyId" element={<PropertyDetailPageWrapper />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/settings/organisation" element={<OrganisationSettingsPageWrapper />} />
        <Route path="/customers" element={<CustomersListPageWrapper />} />
        <Route path="/customers/:customerId" element={<CustomerDetailPageWrapper />} />
        <Route path="/insurance/policies" element={<PoliciesListPageWrapper />} />
        <Route path="/insurance/drafts" element={<PolicyDraftsPageWrapper />} />
        <Route path="/insurance/renewal" element={<RenewalListPageWrapper />} />
        <Route path="/insurance/renewal-import" element={<BulkRenewalImportPageWrapper />} />
        <Route path="/insurance/policies/new" element={<PolicyFormPageWrapper />} />
        <Route path="/insurance/policies/:id" element={<PolicyFormPageWrapper />} />
        <Route path="/insurance/types" element={<InsuranceTypesListPageWrapper />} />
        <Route path="/insurance/types/new" element={<CreateTypeStep1PageWrapper />} />
        <Route path="/insurance/types/new/extract" element={<ExtractPolicyPageWrapper />} />
        <Route path="/insurance/types/migration" element={<MigrationPageWrapper />} />
        <Route path="/insurance/types/:typeId" element={<TypeBuilderPageWrapper />} />
        <Route path="/insurance/types/:typeId/review" element={<ReviewPublishPageWrapper />} />
        <Route path="/insurance/providers" element={<ProvidersListPageWrapper />} />
        <Route path="/insurance/providers/new" element={<ProviderFormPageWrapper />} />
        <Route path="/insurance/providers/:id" element={<ProviderFormPageWrapper />} />
        <Route path="/insurance/companies" element={<CompaniesListPageWrapper />} />
        <Route path="/insurance/companies/:id" element={<CompanyDetailPageWrapper />} />
        <Route
          path="/insurance/companies/:companyId/employees/:employeeId"
          element={<InsuranceEmployeeDetailPageWrapper />}
        />
        <Route path="/insurance/bank-accounts" element={<BankAccountsPageWrapper />} />
        <Route path="/insurance/invoices" element={<InvoicesListPageWrapper />} />
        <Route path="/insurance/invoices/new" element={<InvoiceFormPageWrapper />} />
        <Route path="/insurance/invoices/:id" element={<InvoiceFormPageWrapper />} />
        <Route path="/insurance/payments" element={<PaymentsListPageWrapper />} />
        <Route path="/insurance/claims" element={<ClaimsListPageWrapper />} />
        <Route path="/insurance/claims/:id" element={<ClaimDetailPageWrapper />} />
        <Route path="/insurance/benefit-plans" element={<BenefitPlansListPageWrapper />} />
        <Route path="/insurance/benefit-plans/new" element={<BenefitPlanFormPageWrapper />} />
        <Route path="/insurance/benefit-plans/:id/edit" element={<BenefitPlanFormPageWrapper />} />
        <Route
          path="/insurance/benefit-plans/:id/preview"
          element={<BenefitPlanPreviewPageWrapper />}
        />
        <Route path="/insurance/benefit-plans/:id" element={<BenefitPlanDetailPageWrapper />} />
        <Route path="/insurance/pre-authorizations" element={<PreAuthListPageWrapper />} />
        <Route path="/insurance/pre-authorizations/:id" element={<PreAuthDetailPageWrapper />} />
        <Route path="/insurance/claim-forms" element={<ClaimFormsListPageWrapper />} />
        <Route path="/insurance/claim-forms/:id" element={<ClaimFormEditorPageWrapper />} />
        <Route path="/insurance/settings" element={<InsuranceSettingsPageWrapper />} />
        <Route path="/insurance/knowledge-base" element={<InsuranceKnowledgeBasePageWrapper />} />
        {/* Chat Agents */}
        <Route path="/insurance/chat-agents" element={<ChatSessionHistoryPageWrapper />} />
        <Route path="/insurance/chat-agents/:chatId" element={<ChatSessionDetailPageWrapper />} />
        <Route path="/insurance/voice-agents" element={<VoiceAgentsListPageWrapper />} />
        <Route path="/insurance/voice-agents/calls" element={<VoiceCallHistoryPageWrapper />} />
        <Route
          path="/insurance/voice-agents/calls/:callId"
          element={<VoiceCallDetailPageWrapper />}
        />
        <Route path="/insurance/voice-agents/phones" element={<VoicePhoneNumbersPageWrapper />} />
        <Route path="/insurance/voice-agents/sip" element={<VoiceSipTrunksPageWrapper />} />
        <Route
          path="/real-estate/knowledge-base"
          element={<RealEstateKnowledgeBasePageWrapper />}
        />
        {/* Control Panel */}
        <Route path="/control" element={<ControlDashboardPageWrapper />} />
        <Route path="/control/users" element={<UsersManagementPageWrapper />} />
        <Route path="/control/organisations" element={<OrganisationsManagementPageWrapper />} />
        <Route path="/control/organisations/:id" element={<OrgDetailPageWrapper />} />
        <Route path="/control/templates" element={<PolicyTemplatesPageWrapper />} />
        <Route path="/control/templates/:id" element={<TemplateDetailPageWrapper />} />
        <Route path="/control/audit" element={<AuditLogPageWrapper />} />
        <Route path="/control/policies" element={<OrgPolicyStatsPageWrapper />} />
      </Route>

      {/* Semi-public company invite route */}
      <Route path="/company-invite/:token" element={<CompanyInviteAcceptPage />} />

      {/* Company portal routes */}
      <Route
        path="/company/employees"
        element={
          isAuthenticated ? <CompanyEmployeesListPageWrapper /> : <Navigate to="/signin" replace />
        }
      />
      <Route
        path="/company/employees/new"
        element={
          isAuthenticated ? <CompanyNewEmployeePageWrapper /> : <Navigate to="/signin" replace />
        }
      />
      <Route
        path="/company/employees/:id/edit"
        element={
          isAuthenticated ? <CompanyEditEmployeePageWrapper /> : <Navigate to="/signin" replace />
        }
      />
      <Route
        path="/company/employees/:id"
        element={
          isAuthenticated ? <CompanyEmployeeDetailPageWrapper /> : <Navigate to="/signin" replace />
        }
      />
      <Route
        path="/company/policies"
        element={
          isAuthenticated ? <CompanyPoliciesListPageWrapper /> : <Navigate to="/signin" replace />
        }
      />
      <Route
        path="/company/policies/:id"
        element={
          isAuthenticated ? <CompanyPolicyDetailPageWrapper /> : <Navigate to="/signin" replace />
        }
      />
      <Route
        path="/company/invoices"
        element={
          isAuthenticated ? <CompanyInvoicesListPageWrapper /> : <Navigate to="/signin" replace />
        }
      />
      <Route
        path="/company/invoices/:id"
        element={
          isAuthenticated ? <CompanyInvoiceDetailPageWrapper /> : <Navigate to="/signin" replace />
        }
      />
      <Route
        path="/company/managers"
        element={
          isAuthenticated ? <CompanyManagersPageWrapper /> : <Navigate to="/signin" replace />
        }
      />
      <Route
        path="/company/settings"
        element={
          isAuthenticated ? <CompanySettingsPageWrapper /> : <Navigate to="/signin" replace />
        }
      />
      <Route
        path="/company/claims"
        element={
          isAuthenticated ? <CompanyClaimsSummaryPageWrapper /> : <Navigate to="/signin" replace />
        }
      />
      <Route
        path="/company/my-insurance"
        element={isAuthenticated ? <MyInsurancePageWrapper /> : <Navigate to="/signin" replace />}
      />
      <Route
        path="/company/my-insurance/coverage/:policyId"
        element={isAuthenticated ? <MyCoveragePageWrapper /> : <Navigate to="/signin" replace />}
      />
      <Route
        path="/company/my-insurance/claims"
        element={isAuthenticated ? <MyClaimsListPageWrapper /> : <Navigate to="/signin" replace />}
      />
      <Route
        path="/company/my-insurance/claims/new"
        element={isAuthenticated ? <NewClaimPageWrapper /> : <Navigate to="/signin" replace />}
      />
      <Route
        path="/company/my-insurance/claims/:id"
        element={isAuthenticated ? <MyClaimDetailPageWrapper /> : <Navigate to="/signin" replace />}
      />
      <Route
        path="/company/my-insurance/pre-authorizations"
        element={isAuthenticated ? <MyPreAuthListPageWrapper /> : <Navigate to="/signin" replace />}
      />
      <Route
        path="/company/my-insurance/pre-authorizations/new"
        element={isAuthenticated ? <NewPreAuthPageWrapper /> : <Navigate to="/signin" replace />}
      />
      <Route
        path="/company/my-insurance/pre-authorizations/:id"
        element={
          isAuthenticated ? <MyPreAuthDetailPageWrapper /> : <Navigate to="/signin" replace />
        }
      />

      <Route
        path="/company/my-insurance/apply/new"
        element={isAuthenticated ? <CustomerApplyPageWrapper /> : <Navigate to="/signin" replace />}
      />
      <Route
        path="/company/my-insurance/applications"
        element={
          isAuthenticated ? <MyApplicationsPageWrapper /> : <Navigate to="/signin" replace />
        }
      />

      {/* Customer portal routes */}
      <Route
        path="/my-insurance"
        element={isAuthenticated ? <MyInsurancePageWrapper /> : <Navigate to="/signin" replace />}
      />
      <Route
        path="/my-insurance/coverage/:policyId"
        element={isAuthenticated ? <MyCoveragePageWrapper /> : <Navigate to="/signin" replace />}
      />
      <Route
        path="/my-insurance/claims"
        element={isAuthenticated ? <MyClaimsListPageWrapper /> : <Navigate to="/signin" replace />}
      />
      <Route
        path="/my-insurance/claims/new"
        element={isAuthenticated ? <NewClaimPageWrapper /> : <Navigate to="/signin" replace />}
      />
      <Route
        path="/my-insurance/claims/:id"
        element={isAuthenticated ? <MyClaimDetailPageWrapper /> : <Navigate to="/signin" replace />}
      />
      <Route
        path="/my-insurance/pre-authorizations"
        element={isAuthenticated ? <MyPreAuthListPageWrapper /> : <Navigate to="/signin" replace />}
      />
      <Route
        path="/my-insurance/pre-authorizations/new"
        element={isAuthenticated ? <NewPreAuthPageWrapper /> : <Navigate to="/signin" replace />}
      />
      <Route
        path="/my-insurance/pre-authorizations/:id"
        element={
          isAuthenticated ? <MyPreAuthDetailPageWrapper /> : <Navigate to="/signin" replace />
        }
      />
      <Route
        path="/my-insurance/apply/new"
        element={isAuthenticated ? <CustomerApplyPageWrapper /> : <Navigate to="/signin" replace />}
      />
      <Route
        path="/my-insurance/applications"
        element={
          isAuthenticated ? <MyApplicationsPageWrapper /> : <Navigate to="/signin" replace />
        }
      />

      {/* Dashboard */}
      <Route
        path="/dashboard"
        element={isAuthenticated ? <DashboardPageWrapper /> : <Navigate to="/signin" replace />}
      />

      {/* Default route */}
      <Route
        path="/"
        element={isAuthenticated ? <DefaultRedirect /> : <Navigate to="/signin" replace />}
      />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
